import React, { useState, useEffect, useCallback } from 'react';
import { Gift, Plus, Pencil, Trash2, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';

export default function AdminRewards() {
  const { token, API } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editReward, setEditReward] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    name: '', description: '', type: 'gift_card', brand: '',
    image_url: '', tiers: [{ points: 5000, value: 5, label: '$5' }], active: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchRewards = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/rewards`, { headers });
      setRewards(res.data.rewards);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchRewards(); }, [fetchRewards]);

  const resetForm = () => {
    setForm({ name: '', description: '', type: 'gift_card', brand: '', image_url: '', tiers: [{ points: 5000, value: 5, label: '$5' }], active: true });
    setEditReward(null);
  };

  const openEdit = (reward) => {
    setForm({ name: reward.name, description: reward.description, type: reward.type, brand: reward.brand, image_url: reward.image_url || '', tiers: reward.tiers || [], active: reward.active });
    setEditReward(reward);
    setShowForm(true);
  };

  const openNew = () => { resetForm(); setShowForm(true); };

  const handleSave = async () => {
    try {
      if (editReward) {
        await axios.put(`${API}/admin/rewards/${editReward.id}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/rewards`, form, { headers });
      }
      setShowForm(false);
      resetForm();
      fetchRewards();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reward?')) return;
    try {
      await axios.delete(`${API}/admin/rewards/${id}`, { headers });
      fetchRewards();
    } catch (e) { console.error(e); }
  };

  const toggleActive = async (reward) => {
    try {
      await axios.put(`${API}/admin/rewards/${reward.id}`, { active: !reward.active }, { headers });
      fetchRewards();
    } catch (e) { console.error(e); }
  };

  const addTier = () => {
    setForm(f => ({ ...f, tiers: [...f.tiers, { points: 10000, value: 10, label: '$10' }] }));
  };

  const removeTier = (idx) => {
    setForm(f => ({ ...f, tiers: f.tiers.filter((_, i) => i !== idx) }));
  };

  const updateTier = (idx, field, value) => {
    setForm(f => {
      const tiers = [...f.tiers];
      tiers[idx] = { ...tiers[idx], [field]: field === 'points' ? parseInt(value) || 0 : field === 'value' ? parseFloat(value) || 0 : value };
      return { ...f, tiers };
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl text-white">Rewards</h2>
          <p className="text-sm text-zinc-500">Manage gift cards and cashback rewards</p>
        </div>
        <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-black font-bold gap-2" data-testid="add-reward-btn">
          <Plus className="w-4 h-4" /> Add Reward
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : rewards.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">No rewards configured</div>
      ) : (
        <div className="space-y-3">
          {rewards.map((r) => (
            <div key={r.id} className="bg-[#121214] border border-white/5 rounded-xl p-5 flex items-start gap-4" data-testid={`admin-reward-${r.id}`}>
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center text-lg flex-shrink-0">
                {r.type === 'cashback' ? '💰' : '🎁'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-white">{r.name}</h3>
                  <Badge variant="outline" className={`text-[10px] ${r.active ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}`}>
                    {r.active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] border-white/10 text-zinc-400">{r.type}</Badge>
                </div>
                <p className="text-xs text-zinc-500 mb-2">{r.description}</p>
                <div className="flex gap-1.5 flex-wrap">
                  {r.tiers?.map((t, ti) => (
                    <span key={ti} className="px-2 py-0.5 rounded bg-white/5 text-[10px] text-zinc-400 font-mono">
                      {t.label} = {t.points.toLocaleString()} pts
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-600 mt-2">Redeemed: {r.total_redemptions || 0} times</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} className="data-[state=checked]:bg-amber-500" />
                <Button variant="ghost" size="icon" onClick={() => openEdit(r)} className="text-zinc-500 hover:text-white"><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); resetForm(); }}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editReward ? 'Edit Reward' : 'New Reward'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Name</label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#09090B] border-white/10 text-white" data-testid="reward-name-input" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Brand</label>
                <Input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} className="bg-[#09090B] border-white/10 text-white" placeholder="e.g. Amazon, Apple" />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Description</label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="bg-[#09090B] border-white/10 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full bg-[#09090B] border border-white/10 text-white rounded-md px-3 py-2 text-sm">
                  <option value="gift_card">Gift Card</option>
                  <option value="cashback">Cashback</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Image URL (optional)</label>
                <Input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="bg-[#09090B] border-white/10 text-white" placeholder="https://..." />
              </div>
            </div>

            {/* Tiers */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-zinc-400 font-bold">Redemption Tiers (1000 pts = $1)</label>
                <Button variant="ghost" size="sm" onClick={addTier} className="text-amber-500 text-xs h-7 px-2">+ Add Tier</Button>
              </div>
              <div className="space-y-2">
                {form.tiers.map((tier, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/[0.02] rounded-lg p-2">
                    <Input value={tier.label} onChange={e => updateTier(i, 'label', e.target.value)} className="bg-[#09090B] border-white/10 text-white text-xs w-20" placeholder="$5" />
                    <Input type="number" value={tier.value} onChange={e => updateTier(i, 'value', e.target.value)} className="bg-[#09090B] border-white/10 text-white text-xs w-20" placeholder="Value $" />
                    <Input type="number" value={tier.points} onChange={e => updateTier(i, 'points', e.target.value)} className="bg-[#09090B] border-white/10 text-white text-xs flex-1" placeholder="Points" />
                    <Button variant="ghost" size="icon" onClick={() => removeTier(i)} className="text-zinc-600 hover:text-red-400 h-8 w-8"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={handleSave} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="reward-save-btn">
              {editReward ? 'Update Reward' : 'Create Reward'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
